import type { User } from '@supabase/supabase-js';

import { Mixpanel } from './mixpanel';
import { Analytics } from './tracker';

/**
 * Tracks user sign-in event and updates user profile in all analytics services.
 * - Sets user in Popow CRM (via tracker)
 * - Sets user profile properties in Mixpanel (people.set)
 * - Tracks appropriate event (SignUp Completed or Login Success)
 *
 * ⚠️ IMPORTANT: Mixpanel identify는 호출하지 않음
 * - 신규 사용자: useMixpanelAliasOnFirstSignup 훅에서 alias → identify 처리
 * - 기존 사용자: useIdentifyUserOnLoad 훅에서 identify 처리
 * 이렇게 분리하여 alias가 항상 identify보다 먼저 호출되도록 보장
 *
 * 🎯 sec_uid 우선 정책:
 * - tiktok_sec_uid가 있으면 sec_uid를 distinct_id로 사용
 * - 없으면 email → user.id 순서로 fallback
 *
 * @param user - Supabase user object
 * @param tracker - Analytics tracker instance (optional for backward compatibility)
 * @param tiktokSecUid - The user's TikTok sec_uid from profile (optional)
 */
export const trackSignIn = (
  user: User,
  tracker?: {
    setUser: (distinctId: string, properties: Record<string, unknown>) => void;
  },
  tiktokSecUid?: string | null,
) => {
  const createdAt = new Date(user.created_at).getTime();
  const lastSignInAt = user.last_sign_in_at ? new Date(user.last_sign_in_at).getTime() : createdAt;

  const userProperties = {
    $email: user.email,
    $name: user.user_metadata.username || '',
    created_at: user.created_at,
    tiktok_sec_uid: tiktokSecUid || null, // CRM tracker에서 sec_uid 우선 정책 적용을 위해 전달
  };

  // 🎯 sec_uid 우선 정책: tiktok_sec_uid → email → user.id
  const distinctId = tiktokSecUid || user.email || user.id;

  // 🎯 Popow CRM: setUser 호출 (내부에서 identify 처리)
  if (tracker) {
    tracker.setUser(distinctId, userProperties);
  }

  // 🎯 Mixpanel: people.set만 호출 (identify는 별도 훅에서 처리)
  // identify는 useMixpanelAliasOnFirstSignup 또는 useIdentifyUserOnLoad에서 처리됨
  Mixpanel.people.set(userProperties);

  const isNewUser = Math.abs(lastSignInAt - createdAt) < 2000;

  if (isNewUser) {
    Analytics.track('Complete Signup', {
      user_id: user.id,
      user_email: user.email,
    });
  } else {
    Analytics.track('Complete Login', {
      user_id: user.id,
      user_email: user.email,
    });
  }
};
